import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { BUTTONS, ERRORS, OTHERS, ROLES, TITLES } from 'locales/locales'
import { FORM } from 'locales/locales'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'app/hooks'
import { FixMeLater } from 'types'
import { useLazyGetSystemQuery, useUpdateSystemSettingsMutation } from 'app/services/system.service'
import Loading from 'components/spinner/Loading'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { SUPPORTED_FILE_TYPES } from 'utils/constants'
import { Checkbox } from '@progress/kendo-react-inputs'
import CustomButton from 'components/button/CustomButton'

const FileExtensionSettings: React.FC = () => {
  const { t } = useTranslation()
  const systemSettingsSlice = useAppSelector((state) => state.system.data)

  const [updateSystem] = useUpdateSystemSettingsMutation()
  const [getSystemSettings] = useLazyGetSystemQuery()

  const [fileExtensions, setFileExtensions] = useState<string[]>([])

  useEffect(() => {
    fetchSystemSettings()
  }, [])

  const fetchSystemSettings = useCallback(async () => {
    try {
      Loading.ref.show()
      const response = await getSystemSettings({})
      if (response?.data?.data) {
        setFileExtensions(response?.data?.data?.fileExtensions || [])
      } else {
        setFileExtensions(systemSettingsSlice?.fileExtensions || [])
      }
    } catch (error) {
      console.error('Error fetching system settings:', error)
    } finally {
      Loading.ref.hide()
    }
  }, [getSystemSettings, systemSettingsSlice])

  const handleSubmit = useCallback(async () => {
    try {
      const system: FixMeLater = await updateSystem({
        fileExtensions: fileExtensions,
        _id: systemSettingsSlice?._id,
      })
      if (system?.data?.success) {
        fetchSystemSettings()
      }
    } catch (error) {
      DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.SOMETHING_WENT_WRONG_ERROR),
      })
    }
  }, [updateSystem, fileExtensions, systemSettingsSlice._id, fetchSystemSettings, t])

  const onChangeFileExtension = (value: string) => {
    if (fileExtensions.includes(value)) {
      setFileExtensions(fileExtensions.filter((m) => m !== value))
    } else {
      setFileExtensions([...fileExtensions, value])
    }
  }

  const handleSelectAll = (categoryExtensions: string[]) => {
    const allSelected = categoryExtensions.every(ext => fileExtensions.includes(ext))
    if (allSelected) {
      // Tümünü kaldır
      setFileExtensions(fileExtensions.filter(ext => !categoryExtensions.includes(ext)))
    } else {
      // Eksik olanları ekle
      const newExtensions = [...fileExtensions]
      categoryExtensions.forEach(ext => {
        if (!newExtensions.includes(ext)) {
          newExtensions.push(ext)
        }
      })
      setFileExtensions(newExtensions)
    }
  }

  const handleSelectAllGlobal = () => {
    const allExtensions = SUPPORTED_FILE_TYPES.flatMap(category => 
      category.extensions.map(ext => ext.value)
    )
    const allSelected = allExtensions.every(ext => fileExtensions.includes(ext))
    
    if (allSelected) {
      setFileExtensions([])
    } else {
      setFileExtensions(allExtensions)
    }
  }

  const getSelectedCount = (categoryExtensions: string[]) => {
    return categoryExtensions.filter(ext => fileExtensions.includes(ext)).length
  }

  const getGlobalSelectedCount = () => {
    const allExtensions = SUPPORTED_FILE_TYPES.flatMap(category => 
      category.extensions.map(ext => ext.value)
    )
    return allExtensions.filter(ext => fileExtensions.includes(ext)).length
  }

  return (
    <ContentCard>
      <ContentCardHeader>{t(FORM.FILE_UPLOAD_TITLE)}</ContentCardHeader>
      <ContentCardBody>
        <GlobalSelectionSection>
          <GlobalSelectionHeader>
            <GlobalSelectionTitle>{t(TITLES.ALL_FILE_TYPES)}</GlobalSelectionTitle>
            <GlobalSelectionCount>
              {getGlobalSelectedCount()} / {SUPPORTED_FILE_TYPES.flatMap((cat) => cat.extensions).length} {t(OTHERS.SELECTED)}
            </GlobalSelectionCount>
            <GlobalSelectionButton onClick={handleSelectAllGlobal}>
              {getGlobalSelectedCount() ===
              SUPPORTED_FILE_TYPES.flatMap((cat) => cat.extensions).length
                ? t(OTHERS.REMOVE_ALL)
                : t(ROLES.SELECT_ALL)}
            </GlobalSelectionButton>
          </GlobalSelectionHeader>
        </GlobalSelectionSection>

        <FileTypesGrid>
          {SUPPORTED_FILE_TYPES.map((category, cIndex) => {
            const categoryExtensions = category.extensions.map((ext) => ext.value)
            const selectedCount = getSelectedCount(categoryExtensions)
            const totalCount = categoryExtensions.length

            return (
              <CategoryCard key={category.name + cIndex}>
                <CategoryHeader>
                  <CategoryTitle>{t(category.name)}</CategoryTitle>
                  <CategoryCount>
                    {selectedCount} / {totalCount} {t(OTHERS.SELECTED)}
                  </CategoryCount>
                  <CategorySelectAllButton
                    onClick={() => handleSelectAll(categoryExtensions)}
                    isAllSelected={selectedCount === totalCount}
                  >
                    {selectedCount === totalCount ? t(OTHERS.REMOVE_ALL) : t(ROLES.SELECT_ALL)}
                  </CategorySelectAllButton>
                </CategoryHeader>

                <ExtensionsGrid>
                  {category.extensions.map((mimeType, index) => (
                    <ExtensionItem
                      key={mimeType.value + index}
                      onClick={() => onChangeFileExtension(mimeType.value)}
                    >
                      <ExtensionCheckbox>
                        <Checkbox
                          checked={fileExtensions?.some((ex) => ex === mimeType.value)}
                          onChange={() => onChangeFileExtension(mimeType.value)}
                        />
                      </ExtensionCheckbox>
                      <ExtensionName>{mimeType.name}</ExtensionName>
                    </ExtensionItem>
                  ))}
                </ExtensionsGrid>
              </CategoryCard>
            )
          })}
        </FileTypesGrid>

        <VimeButtonWrapper>
          <CustomStyledButton text={t(BUTTONS.SAVE)} textColor="white" onClick={handleSubmit} />
        </VimeButtonWrapper>
      </ContentCardBody>
    </ContentCard>
  )
}

export default FileExtensionSettings

const ContentCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e8eaed;
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 24px;
`

const GlobalSelectionSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  color: white;
`

const GlobalSelectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const GlobalSelectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`

const GlobalSelectionCount = styled.span`
  font-size: 18px;
  opacity: 0.9;
`

const GlobalSelectionButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  min-width: 130px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
  }
`

const FileTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`

const CategoryCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #d0d7de;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`

const CategoryTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
`

const CategoryCount = styled.span`
  font-size: 13px;
  color: #666;
  font-weight: 500;
`

const CategorySelectAllButton = styled.button<{ isAllSelected: boolean }>`
  background: ${props => props.isAllSelected ? '#dc3545' : '#28a745'};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.isAllSelected ? '#c82333' : '#218838'};
    transform: translateY(-1px);
  }
`

const ExtensionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
`

const ExtensionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e8eaed;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #e9ecef;
    border-color: #d0d7de;
  }
`

const ExtensionCheckbox = styled.div`
  margin-right: 8px;
`

const ExtensionName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  user-select: none;
`

const CustomStyledButton = styled(CustomButton)`
  background-color: #1f4ab7;
  border-radius: 6px;
  padding: 12px 24px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background-color: #1a3f9e;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(31, 74, 183, 0.3);
  }
`

const VimeButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`
